import { useState, useCallback } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";

export type ProviderConfig = {
  label?: string;
  prompt_type?: string;
  hosted_domain?: string;
};

export type SiteInfo = {
  blog_id: number;
  blog_name: string;
  subdomain: string;
  auth_methods: {
    password: boolean;
    google: boolean;
    microsoft: boolean;
  };
  default_method: string;
  provider_config?: Record<string, ProviderConfig>;
};

export type IdentityLookupResult =
  | { found: true; multiple: false; site: SiteInfo }
  | { found: true; multiple: true; sites: SiteInfo[] }
  | { found: false };

export const useIdentityLookup = () => {
  const [result, setResult] = useState<IdentityLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getApiUrl } = useMultiSiteContext();

  const lookup = useCallback(
    async (identifier: string): Promise<IdentityLookupResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          getApiUrl("wp-json/smassets/v1/auth/identify"),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier }),
          }
        );
        if (!res.ok) {
          setError("Something went wrong. Please try again.");
          return null;
        }
        const data: IdentityLookupResult = await res.json();
        setResult(data);
        return data;
      } catch {
        setError("Unable to reach the server. Please try again.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [getApiUrl]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { lookup, result, loading, error, reset };
};
